<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <div class="row">
        <div class="col-md-12">
          <h3>{{ _('Data import') }}</h3>
        </div>
      </div>
      <div class="row" v-if="!items.length">
        <div class="col-md-12">
          <div class="well">
            <p>{{ _('You can import data to Johku by sending a formatted XLS or CSV file.') }}</p>
            <p>{{ _('Currently supported types:') }}</p>
            <ul class="list-supported-types">
              <li>{{ _('Products') }}</li>
              <li>{{ _('Customers') }}</li>
            </ul>
            <a target="_blank" href="https://support.johku.com/Johku-UKK/Tuotteiden-luominen/Tuotteiden-luominen-Tietojen-tuonti--toiminnolla">{{ _('See support for instructions') }}</a>
          </div>
          <file-uploader :target="targetUrl" v-on:done="fileUploaded" accept=".csv,.xlsx"/>
        </div>
      </div>
      <div class="row" v-if="items.length">
        <div class="col-md-12">
          <div class="btn-group mb-3" role="group" aria-label="Import type selector">
            <button
              type="button"
              class="btn"
              :class="importType === 'products' ? 'btn-primary' : 'btn-outline-primary'"
              @click="importType = 'products'"
            >
              {{ _('Import products') }}
            </button>
            <button
              type="button"
              class="btn"
              :class="importType === 'customers' ? 'btn-primary' : 'btn-outline-primary'"
              @click="importType = 'customers'"
            >
              {{ _('Import customers') }}
            </button>
          </div>
        </div>
      </div>
      <div class="row" v-if="items.length">
        <div class="col-md-12">
          <table class="table table-striped">
            <thead>
              <tr>
                <th scope="col" v-for="title in titles">
                  <select class="form-control" v-model="titleMap[title]">
                    <option v-for="field in fields" :value="field.value">{{ field.title }}</option>
                  </select>
                </th>
              </tr>
              <tr>
                <th scope="col" v-for="title in titles">
                  {{ title }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in items">
                <td v-for="title in titles">{{ item[title] }}</td>
              </tr>
            </tbody>
          </table>
          <div class="form-group" v-if="categories.length && importType === 'products'">
            <label for="categorySelector">{{ _('Add to category') }}</label>
            <select class="form-control" id="categorySelector" v-model="category">
              <option value="">- {{ _('Do not add') }} -</option>
              <option v-for="category in categories" :value="category.id">{{ category.name }}</option>
            </select>
          </div>
          <button class="btn btn-success" v-on:click="doImport" :disabled="!isImportValid"><template v-if="importType === 'products'">{{ _('Import products') }}</template><template v-if="importType === 'customers'">{{ _('Import customers') }}</template></button>
          <button class="btn btn-default" v-on:click="cancelImport">{{ _('Cancel') }}</button>
        </div>
      </div>
      <div class="row" v-if="successfullyImported">
        <div class="col-md-12">
          <h4>{{ _('Import succeeded') }}</h4>
          <button class="btn btn-success" v-on:click="ack">{{ _('OK') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import axios from 'axios'
import components from '~/lib/components'

export default {
  data () {
    const _ = useNuxtApp().$translate
    return {
      items: [],
      categories: [],
      import: null,
      titleMap: {},
      category: null,
      status: null,
      importType: 'products',
      productFields: [
        {
          value: 'ignore',
          title: _('Ignore')
        },
        { 
          value: 'name', 
          title: _('Name')
        },
        { 
          value: 'price', 
          title: _('Price')
        },
        { 
          value: 'count', 
          title: _('Stock balance')
        },
        { 
          value: 'vat', 
          title: _('VAT')
        },
        { 
          value: 'ean', 
          title: _('Barcode')
        },
        { 
          value: 'netvisorid', 
          title: _('Netvisor key')
        }
      ],
      customerFields: [
        {
          value: 'ignore',
          title: _('Ignore')
        },
        {
          value: 'name',
          title: _('Name')
        },
        {
          value: 'address',
          title: _('Address')
        },
        {
          value: 'zip',
          title: _('Postal code')
        },
        {
          value: 'city',
          title: _('City')
        },
        {
          value: 'country',
          title: _('Country')
        },
        {
          value: 'phone',
          title: _('Phone')
        },
        { 
          value: 'email',
          title: _('Email')
        },
      ]
    }
  },
  mixins: [components],
  computed: {
    fields () {
      switch (this.importType) {
        case 'products':
          return this.productFields
        case 'customers':
          return this.customerFields
      }
    },
    data () {
      return JSON.parse(this.component.data)
    },
    targetUrl () {
      return this.baseDomain + this.shopId + '/importer.json?apikey=' + this.apiKey
    },
    titles () {
      if (!this.items.length) {
        return []
      }
      let item = this.items[0]
      return Object.keys(item)
    },
    successfullyImported () {
      return this.status === 'success'
    },
    isImportValid () {
      if (!this.items || this.items.length === 0) {
        return false
      }
      if (this.titleMap[this._('Name')] === 'ignore') {
        return false
      }
      if (this.importType === 'customers' && this.titleMap[this._('Email')] === 'ignore') {
        return false
      }
      return true
    }
  },
  methods: {
    selectPos (pos) {
      this.selectedPos = pos
    },
    async fileUploaded (id) {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/importer/' + id + '.json?apikey=' + this.apiKey)
      } catch (e) {
        return this.showError(e)
      }
      this.import = response.data
      this.items = JSON.parse(response.data.data)
      // Detect if any column values look like email addresses
      let foundEmail = false
      for (let i = 0; i < this.items.length && !foundEmail; i++) {
        let row = this.items[i]
        for (let key in row) {
          if (typeof row[key] === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row[key])) {
            foundEmail = true
            break
          }
        }
      }
      if (foundEmail) {
        this.importType = 'customers'
      } else {
        this.importType = 'products'
      }
      for (var i = 0; i < this.titles.length; i++) {
        let t = this.titles[i]
        for (var j = 0; j < this.fields.length; j++) {
          if (this.fields[j].value === t) {
            this.titleMap[t] = t
          }
        }
      }
    },
    async doImport () {
      let response
      try {
        let data = {
          id: this.import.id,
          import: true,
          titleMap: this.titleMap
        }
        if (this.category) {
          data.category = this.category
        }
        response = await axios.put(this.baseDomain + this.shopId + '/importer/' + this.import.id + '.json?apikey=' + this.apiKey, data)
      } catch (e) {
        return this.showError(e)
      }
      this.items = []
      this.status = 'success'
    },
    cancelImport () {
      this.status = null
      this.items = []
    },
    ack () {
      this.status = null
    }
  },
  watch: {
    importType (newVal, oldVal) {
      if (newVal !== oldVal) {
        // Reset all mappings to 'ignore' when switching import type
        for (var i = 0; i < this.titles.length; i++) {
          let t = this.titles[i]
          this.titleMap[t] = 'ignore'
        }
        // Try to auto-detect mappings for the new type
        for (var i = 0; i < this.titles.length; i++) {
          let t = this.titles[i]
          for (var j = 0; j < this.fields.length; j++) {
            if (this.fields[j].value === t) {
              this.titleMap[t] = t
            }
          }
        }
      }
    }
  },
  async mounted () {
    let response
    try {
      response = await axios.get(this.baseDomain + this.shopId + '/categories.json?apikey=' + this.apiKey + '&iSortingCols=1&iSortCol_0=2&sSortDir_0=asc')
    } catch(e) {}
    this.categories = response.data
  }
}
</script>

<style scoped>
.list-supported-types li {
  font-weight: bold;
}
</style>